import React, { useState, useRef, useEffect } from 'react'
import styled, { keyframes } from 'styled-components'
import SubmitButton from '../../../atoms/Login/SubmitButton'
import { useNavigate } from 'react-router-dom'
import {
  clearError,
  clearToken,
} from '../../../../Redux/Slice/Auth/AuthSlice'
import { resendOTP, verifyOTP } from '../../../../Redux/ApiCalls/Auth/login'
import LoadingDots from '../../../atoms/Utlis/LoadinDots'
import { showErrorToast, showSuccessToast } from '../../../atoms/Utlis/Toast'
import { useAppDispatch, useAppSelector } from '../../../../Redux/Hooks'
import sendFirebaseEvent from '../../../../analytics/FirebaseAnalytics'
import { trackClarityEvent } from '../../../../analytics/ClarityAnalytics'
import { CLARITY_EVENTS } from '../../../../analytics/EventTypes'
import { getDefaultRouteForUser, getStoredStudioAccess } from '../../../../Utils/permissionUtils'

interface otpTextProps {
  isActivated: boolean
}

const popIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(16px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`

const OtplVerificationFormContainer = styled.div`
  width: 100%;
  padding: 2.25rem 1.85rem 1.85rem;
  color: #141414;
  border-radius: 24px;
  background:
    linear-gradient(90deg, #ffc951, #ce56b0) top center / 100% 3px no-repeat,
    #ffffff;
  box-shadow:
    0 24px 48px rgba(0, 0, 0, 0.4),
    0 0 0 1px rgba(255, 255, 255, 0.08);
  position: relative;
  box-sizing: border-box;
  overflow: visible;
  animation: ${popIn} 0.55s cubic-bezier(0.22, 1, 0.36, 1) both;

  @media (min-width: 1440px) {
    padding: 2.75rem 2.35rem 2.15rem;
  }

  @media (max-width: 480px) {
    border-radius: 20px;
    padding: 1.75rem 1.35rem 1.5rem;
  }
`

const Form = styled.form`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 1.35rem;
  align-items: stretch;
`

const StepRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`

const StepIndex = styled.span`
  font-family: 'Urbanist', sans-serif;
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: #ce56b0;
`

const ProgressTrack = styled.div`
  flex: 1;
  height: 3px;
  border-radius: 999px;
  background: rgba(20, 20, 20, 0.08);
  overflow: hidden;
`

const ProgressFill = styled.div`
  width: 78%;
  height: 100%;
  border-radius: inherit;
  background: linear-gradient(90deg, #ffc951, #ce56b0);
`

const HeadingGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.55rem;
  width: 100%;
`

const FormHeading = styled.h2`
  font-family: 'Urbanist', sans-serif;
  font-size: clamp(28px, 4.5vw, 36px);
  font-weight: 800;
  line-height: 1.1;
  letter-spacing: -0.03em;
  color: #141414;
  margin: 0;
`

const HeadingAccent = styled.em`
  font-style: normal;
  background: linear-gradient(144.01deg, rgba(255, 201, 81, 0.85) 0%, rgba(206, 86, 176, 0.85) 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`

const SubHeadingText = styled.p`
  font-family: 'Urbanist', sans-serif;
  font-weight: 500;
  font-size: 15px;
  line-height: 1.5;
  color: #5c5650;
  margin: 0;
`

const PhoneHint = styled.span`
  font-weight: 700;
  color: #141414;
`

const InputFields = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 1.2rem;
`

const Label = styled.span`
  font-family: 'Urbanist', sans-serif;
  font-weight: 700;
  font-size: 13px;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #6a635c;
`

const InputContainer = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 10px;
  width: 100%;
`

const OtpInput = styled.input`
  flex: 1;
  min-width: 0;
  max-width: 72px;
  height: 58px;
  text-align: center;
  font-family: 'Urbanist', sans-serif;
  font-size: 24px;
  font-weight: 800;
  color: #141414;
  background: #ffffff;
  border: 1.5px solid rgba(20, 20, 20, 0.08);
  border-radius: 16px;
  box-shadow: 0 2px 8px rgba(20, 20, 20, 0.04);
  outline: none;
  transition: border-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease;

  &:focus {
    border-color: #ce56b0;
    box-shadow:
      0 0 0 4px rgba(206, 86, 176, 0.14),
      0 10px 24px rgba(206, 86, 176, 0.1);
    transform: translateY(-1px);
  }

  &:not(:placeholder-shown) {
    border-color: rgba(206, 86, 176, 0.35);
  }
`

const ResendOtpContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
  min-height: 32px;
`

const ResesndOtpText = styled.p<otpTextProps>`
  font-family: 'Urbanist', sans-serif;
  font-size: 15px;
  font-weight: 600;
  line-height: 1.4;
  margin: 0;
  background: ${(props) =>
    props.isActivated
      ? 'linear-gradient(144.01deg, rgba(255, 201, 81, 0.95) 0%, rgba(206, 86, 176, 0.95) 100%)'
      : 'none'};
  -webkit-background-clip: ${(props) => (props.isActivated ? 'text' : 'border-box')};
  -webkit-text-fill-color: ${(props) => (props.isActivated ? 'transparent' : '#8a847c')};
  background-clip: ${(props) => (props.isActivated ? 'text' : 'border-box')};
  color: ${(props) => (props.isActivated ? 'transparent' : '#8a847c')};
  cursor: ${(props) => (props.isActivated ? 'pointer' : 'default')};
  text-decoration: ${(props) => (props.isActivated ? 'underline' : 'none')};
  text-underline-offset: 3px;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: ${(props) => (props.isActivated ? 0.85 : 1)};
  }
`

const Timer = styled.p`
  font-family: 'Urbanist', sans-serif;
  font-size: 15px;
  font-weight: 700;
  line-height: 1.4;
  margin: 0;
  color: #141414;
  font-variant-numeric: tabular-nums;
`

const BackLink = styled.button`
  font-family: 'Urbanist', sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: #6a635c;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  transition: color 0.2s ease;

  &:hover {
    color: #ce56b0;
  }
`

const FooterRow = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
  margin-top: 0.15rem;
`

const SubmitButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 0.25rem;
  position: relative;
  min-height: 54px;
  width: 100%;
`

const OtplVerificationForm: React.FC = () => {
  const [timer, setTimer] = useState<number>(60)
  const [showResendLink, setShowResendLink] = useState<boolean>(false)
  const [activeButton, setActiveButton] = useState<boolean>(false)
  const [otp, setOtp] = useState<string[]>(['', '', '', ''])
  const inputsRef = useRef<HTMLInputElement[]>([])
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const {
    isError,
    phone_number,
    country_code,
    error,
    user,
    isAuthenticated,
    loading,
    orderId,
    isRegister,
  } = useAppSelector((state) => state.auth)

  useEffect(() => {
    sendFirebaseEvent(
      'OTP_Verification',
      'Preview_Page',
      'load_page',
      'load_OTP_verification_page'
    )
  }, [])

  useEffect(() => {
    if (timer > 0) {
      const countdown = setInterval(() => {
        setTimer((prevTimer) => prevTimer - 1)
      }, 1000)
      return () => clearInterval(countdown)
    } else {
      setShowResendLink(true)
    }
  }, [timer])

  useEffect(() => {
    if (!orderId) {
      navigate('/auth/login')
    }
    return () => {
      dispatch(clearError())
    }
  }, [orderId])

  useEffect(() => {
    if (isError) {
      if (error.status === 401) {
        showErrorToast('Please Register first')
      } else if (error.message) {
        trackClarityEvent(CLARITY_EVENTS.API_ERROR, {
          source: 'otp_verification_form',
          message: error.message,
          status: error.status,
        })
        sendFirebaseEvent('OTP_Verification', 'Error', 'error', error.message)
        showErrorToast(error.message)
      } else {
        showErrorToast('Something went wrong! Please try again later.')
      }
    }
    if (isAuthenticated === true && user && Object.keys(user).length !== 0) {
      const storedAccess = getStoredStudioAccess();
      const isOwner = user.role === 3 || storedAccess?.is_studio_owner;
      const hasSubUserRole = user.role === 4 && (
        Boolean(storedAccess?.studio_role) ||
        (Array.isArray(storedAccess?.permissions) && (storedAccess?.permissions?.length ?? 0) > 0)
      );

      if (isOwner || hasSubUserRole) {
        showSuccessToast('User verified.')
        const targetRoute = getDefaultRouteForUser(storedAccess, user.role);
        navigate(targetRoute)
      } else {
        showErrorToast(
          user.role === 4
            ? "Access denied. You do not have an assigned studio role. Please contact the studio owner."
            : `Access denied. You don't have permission to access this portal.`
        )
        navigate(-1)
        dispatch(clearToken())
      }
    }
    return () => {
      dispatch(clearError())
    }
  }, [isError, isAuthenticated, user])

  useEffect(() => {
    if (isRegister) {
      navigate('/auth/signup')
    }
  }, [isRegister])

  const handleSubmit = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    const otpValue = otp.join('')
    sendFirebaseEvent(
      'OTP_Verification',
      'User_Interaction',
      'OTP_Verification_button_clicked',
      phone_number
    )
    trackClarityEvent(CLARITY_EVENTS.OTP_VERIFICATION_CLICKED, {
      phone_number,
      country_code,
      order_id: orderId,
      otp_length: otpValue.length,
      is_complete: otpValue.length === 4,
      source: 'otp_verification_form',
    })
    if (otpValue.length === 4 && otp.every((digit) => /^\d$/.test(digit))) {
      dispatch(
        verifyOTP({
          phone_number,
          country_code,
          orderId: orderId,
          otp: otpValue,
        })
      )
    }
  }

  const handleResendOtp = (e: React.MouseEvent<HTMLParagraphElement>) => {
    e.preventDefault()
    sendFirebaseEvent(
      'OTP_Verification',
      'User_Interaction',
      'Resend_OTP_button_clicked',
      `resend_OTP_${phone_number}`
    )
    trackClarityEvent(CLARITY_EVENTS.OTP_RESEND_CLICKED, {
      phone_number,
      country_code,
      order_id: orderId,
      source: 'otp_verification_form',
    })
    dispatch(resendOTP({ orderId, country_code }))
    setTimer(60)
    setShowResendLink(false)
  }

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    index: number
  ) => {
    const value = e.target.value
    if (/^\d$/.test(value) || value === '') {
      const newOtp = [...otp]
      newOtp[index] = value
      setOtp(newOtp)
      if (newOtp.join('').length === 4) {
        setActiveButton(true)
      }
      if (value !== '' && index < 3) {
        inputsRef.current[index + 1].focus()
      }
    }
  }

  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    index: number
  ) => {
    if (e.key === 'Backspace') {
      if (otp[index] === '' && index > 0) {
        inputsRef.current[index - 1].focus()
      }
      setActiveButton(false)
    }
  }

  const maskedPhone =
    phone_number && phone_number.length >= 4
      ? `${country_code || ''} ••••${phone_number.slice(-4)}`
      : phone_number

  return (
    <OtplVerificationFormContainer>
      <Form>
        <StepRow>
          <StepIndex>Studio verify · 02</StepIndex>
          <ProgressTrack>
            <ProgressFill />
          </ProgressTrack>
        </StepRow>

        <HeadingGroup>
          <FormHeading>
            Verify your <HeadingAccent>access</HeadingAccent>
          </FormHeading>
          <SubHeadingText>
            Enter the 4-digit OTP sent to{' '}
            <PhoneHint>{maskedPhone || 'your registered phone'}</PhoneHint> to
            continue to your studio dashboard.
          </SubHeadingText>
        </HeadingGroup>

        <InputFields>
          <Label>Studio OTP</Label>
          <InputContainer>
            {otp.map((digit, index) => (
              <OtpInput
                key={index}
                type="text"
                inputMode="numeric"
                autoComplete={index === 0 ? 'one-time-code' : 'off'}
                maxLength={1}
                value={digit}
                placeholder="·"
                onChange={(e) => handleChange(e, index)}
                onKeyDown={(e) => handleKeyDown(e, index)}
                ref={(el) => (inputsRef.current[index] = el!)}
              />
            ))}
          </InputContainer>

          <ResendOtpContainer>
            {showResendLink ? (
              <ResesndOtpText isActivated={true} onClick={handleResendOtp}>
                Resend OTP
              </ResesndOtpText>
            ) : (
              <>
                <ResesndOtpText isActivated={false}>
                  Resend OTP in
                </ResesndOtpText>
                <Timer>00:{timer >= 10 ? timer : `0${timer}`}</Timer>
              </>
            )}
          </ResendOtpContainer>

          <SubmitButtonContainer>
            {loading ? (
              <LoadingDots />
            ) : (
              <SubmitButton
                onClick={handleSubmit}
                width={100}
                isPercent={true}
                text="Verify & continue"
                needArrow={true}
                active={loading ? true : !activeButton}
                textAlign="left"
              />
            )}
          </SubmitButtonContainer>

          <FooterRow>
            <BackLink type="button" onClick={() => navigate('/auth/login')}>
              ← Use a different number
            </BackLink>
          </FooterRow>
        </InputFields>
      </Form>
    </OtplVerificationFormContainer>
  )
}

export default OtplVerificationForm
